import React from "react";
import { SanityImage } from "../../model/common";
import { defaultQuality, imageUrlBuilder } from "../../utils/images";
import {
  ImageComponent,
  useBlockContentImageOptions
} from "../BlockContent/BlockContent";
import Media from "../MediaBlock/MediaBlock";
import styles from "./ImageBlock.module.scss";
import classNames from "classnames";
import { shouldSkipMissingImageAsset } from "../Image/Image";
import { internalLinkUrl } from "../InternalLink/InternalLink";
import { usePathname } from "../../utils/usePathname";
import { useLanguage } from "../../i18n/LanguageContext";

interface ImageBlockProps {
  className?: string;
  node: SanityImage;
  width?: number;
  loading?: "eager" | "lazy" | "auto";
  quality?: number;
}

const ImageBlock = ({
  className,
  node,
  width,
  loading = "lazy",
  quality = defaultQuality
}: ImageBlockProps) => {
  const { attribution, caption, halfWidth, externalLink, internalLink } = node;
  const options = useBlockContentImageOptions();
  const pathname = usePathname();
  const language = useLanguage();
  const to = internalLink
    ? internalLinkUrl(
        internalLink.reference,
        internalLink.query,
        internalLink.fragment,
        language,
        pathname
      )
    : externalLink || "";
  return (
    <Media
      className={className}
      halfWidth={halfWidth}
      to={to}
      caption={caption}
      attribution={attribution}
      mediaItem={
        options.Image ? (
          <options.Image
            loading={loading}
            node={node}
            quality={quality}
            width={width}
          />
        ) : null
      }
    />
  );
};

export const ImageBlockImage: ImageComponent = ({
  loading,
  width,
  node,
  quality,
  className
}) => {
  const { alt, rounded, asset } = node;
  if (shouldSkipMissingImageAsset(node)) {
    return null;
  }
  return (
    <img
      className={classNames(rounded && styles.rounded, className)}
      alt={alt || asset?.altText || ""}
      loading={loading === "auto" ? undefined : loading}
      width={width}
      height={
        width
          ? width / node.asset?.metadata?.dimensions?.aspectRatio
          : undefined
      }
      src={
        imageUrlBuilder
          .image(node)
          .width(width!) // Dubious given types but long-standing behaviour
          .fit("max")
          .quality(quality)
          .url()!
      }
    />
  );
};

export default ImageBlock;
